import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { motion, useAnimation, PanInfo } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Cpu, Clock, Compass, Bell, ShieldCheck, 
  Map, Smartphone, BarChart3 , FuelIcon, WalletCards
} from 'lucide-react';

const cardColors = [
  'bg-blue-50 border-blue-100',
  'bg-emerald-50 border-emerald-100',
  'bg-purple-50 border-purple-100',
  'bg-amber-50 border-amber-100',
  'bg-red-50 border-red-100',
  'bg-indigo-50 border-indigo-100',
  'bg-pink-50 border-pink-100',
  'bg-teal-50 border-teal-100'
];

const iconColors = [
  'text-blue-600 bg-blue-100',
  'text-emerald-600 bg-emerald-100',
  'text-purple-600 bg-purple-100',
  'text-amber-600 bg-amber-100',
  'text-red-600 bg-red-100',
  'text-indigo-600 bg-indigo-100',
  'text-pink-600 bg-pink-100',
  'text-teal-600 bg-teal-100'
];

const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
}> = React.memo(({ icon, title, description, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`rounded-2xl p-6 md:p-8 flex flex-col items-center text-center transition-all duration-300 transform hover:-translate-y-2 flex-shrink-0 w-[280px] sm:w-[320px] mx-3 md:mx-4 border-2 ${cardColors[index]} hover:shadow-lg`}
      style={{ minHeight: '280px' }}
    >
      <div className={`p-3 md:p-4 rounded-xl mb-4 md:mb-6 ${iconColors[index]}`}>
        {React.cloneElement(icon as React.ReactElement, { className: "w-6 h-6 md:w-8 md:h-8" })}
      </div>
      <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2 md:mb-3">{title}</h3>
      <p className="text-sm md:text-[15px] text-gray-600 leading-relaxed px-2 md:px-0">{description}</p>
    </motion.div>
  );
});
FeatureCard.displayName = 'FeatureCard';

const features = [
  { icon: <Cpu />, title: "AI-Powered Routes", description: "Machine learning algorithms analyze traffic patterns" },
  { icon: <Clock />, title: "Time Prediction", description: "Accurate arrival time predictions based on real-time data" },
  { icon: <Compass />, title: "Smart Navigation", description: "Turn-by-turn directions with voice assistance" },
  { icon: <Bell />, title: "Smart Alerts", description: "Get notified about delays and route changes" },
  { icon: <FuelIcon/>, title: "Fuel Optimization", description: "Route safety ratings and emergency assistance" },
  { icon: <Map />, title: "Multi-Modal Planning", description: "Combine buses, trains, and other transport options" },
  { icon: <Smartphone />, title: "Mobile App", description: "Access all features on the go with our app" },
  { icon: <WalletCards />, title: "Smart Card System ", description: "Personalized insights about your travel patterns" }
];

const Features: React.FC = React.memo(() => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();
  const [isDragging, setIsDragging] = useState(false);
  const [currentX, setCurrentX] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  // Memoize duplicated features
  const duplicatedFeatures = useMemo(() => [...features, ...features, ...features], []);

  useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout>;
    const calculateWidths = () => {
      if (contentRef.current && containerRef.current) {
        const contentWidth = contentRef.current.scrollWidth / 3;
        const containerWidth = containerRef.current.offsetWidth;
        setContentWidth(contentWidth);
        setContainerWidth(containerWidth);
        setCurrentX(0);
      }
    };

    calculateWidths();
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(calculateWidths, 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  useEffect(() => {
    if (!isDragging && contentWidth > 0) {
      const duration = Math.max(15, contentWidth / 100); // Adjust speed here
      
      const animate = async () => {
        await controls.start({
          x: -contentWidth,
          transition: { duration, ease: 'linear' }
        });
        
        // Reset position without animation for seamless loop
        controls.set({ x: 0 });
        setCurrentX(0);
        
        // Restart animation
        animate();
      };
      
      animate();
    }
  }, [controls, contentWidth, isDragging]);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
    controls.stop();
  }, [controls]);

  const handleDragEnd = useCallback((event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(false);
    setCurrentX(info.point.x);
  }, []);

  return (
    <section id="features" className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:!bg-ink-850 overflow-hidden">
      <div className="container mx-auto">
        <motion.div 
          ref={ref}
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-black tracking-tight mb-3 md:mb-4" style={{ fontSize: "clamp(28px, 4vw, 48px)", color: "#1D3047" }}>
            <span style={{ color: "#2D5F9E" }}>Intelligent Features for </span>
            <span style={{ color: "#E3B02E" }}>Smarter Travel</span>
          </h2>
          <p className="max-w-3xl mx-auto text-gray-600 text-sm sm:text-[15px] md:text-base px-2 sm:px-0">
            Discover how our AI-powered platform transforms your daily commute.
          </p>
        </motion.div>

        <div className="relative">
          <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none"></div>
          
          <div 
            ref={containerRef}
            className="overflow-x-auto py-6 md:py-8 scrollbar-hide"
          >
            <motion.div
              ref={contentRef}
              className="flex"
              drag="x"
              dragConstraints={{ left: -contentWidth * 2, right: 0 }}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              animate={controls}
              style={{ x: currentX }}
            >
              {duplicatedFeatures.map((feature, index) => (
                <FeatureCard 
                  key={`${index}-${feature.title}`}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  index={index % features.length}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </div>
<style>{`.scrollbar-hide { -webkit-overflow-scrolling: touch; scrollbar-width: none; -ms-overflow-style: none; } .scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>

      
    </section>
  );
});

Features.displayName = 'Features';
export default Features;